import React, { useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';
import { toast } from 'react-toastify';
import {
  Grid, Box, Typography, Chip, Card, CardContent, LinearProgress,
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Paper,
  Select, MenuItem, FormControl, InputLabel, Tabs, Tab, IconButton, Tooltip
} from '@mui/material';
import RefreshIcon from '@mui/icons-material/Refresh';
import HealthAndSafetyIcon from '@mui/icons-material/HealthAndSafety';
import StorageIcon from '@mui/icons-material/Storage';
import CachedIcon from '@mui/icons-material/Cached';
import NotificationsActiveIcon from '@mui/icons-material/NotificationsActive';
import HistoryIcon from '@mui/icons-material/History';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import { showLoader } from '../../../../Redux/Slices/loaderSlice';
import {
  getSystemHealth, getCacheStats, clearCache, getAuditLogs,
  getStatusSummary, getPaymentStats, triggerReminders, runSeeds, runMigration
} from '../../../../Redux/Actions/action';
import ErrorHandler from '../../../../Utils/handler';
import ButtonContained from '../../../Common/ButtonContained';

const SystemManagement = () => {
  const dispatch = useDispatch();
  const [activeTab, setActiveTab] = useState(0);
  const [health, setHealth] = useState(null);
  const [cacheStats, setCacheStats] = useState(null);
  const [auditLogs, setAuditLogs] = useState([]);
  const [statusSummary, setStatusSummary] = useState({});
  const [paymentStats, setPaymentStats] = useState({});
  const [logFilters, setLogFilters] = useState({ category: '', page: 1, limit: 20 });

  // Fetch system health
  const fetchHealth = async () => {
    try {
      const result = await dispatch(getSystemHealth());
      const error = ErrorHandler(result);
      if (error) {
        setHealth(result?.payload?.data?.health);
      }
    } catch (error) {
      console.error('Failed to fetch health');
    }
  };

  // Fetch cache stats
  const fetchCacheStats = async () => {
    try {
      const result = await dispatch(getCacheStats());
      const error = ErrorHandler(result);
      if (error) {
        setCacheStats(result?.payload?.data);
      }
    } catch (error) {
      console.error('Failed to fetch cache stats');
    }
  };

  // Fetch audit logs
  const fetchAuditLogs = async () => {
    dispatch(showLoader(true));
    try {
      const result = await dispatch(getAuditLogs(logFilters));
      const error = ErrorHandler(result);
      if (error) {
        setAuditLogs(result?.payload?.data?.logs || []);
      }
    } catch (error) {
      toast.error('Failed to fetch audit logs');
    } finally {
      dispatch(showLoader(false));
    }
  };

  // Fetch status summary
  const fetchStatusSummary = async () => {
    try {
      const result = await dispatch(getStatusSummary());
      const error = ErrorHandler(result);
      if (error) {
        setStatusSummary(result?.payload?.data?.summary || {});
      }
    } catch (error) {
      console.error('Failed to fetch status summary');
    }
  };

  // Fetch payment stats
  const fetchPaymentStats = async () => {
    try {
      const result = await dispatch(getPaymentStats());
      const error = ErrorHandler(result);
      if (error) {
        setPaymentStats(result?.payload?.data?.stats || {});
      }
    } catch (error) {
      console.error('Failed to fetch payment stats');
    }
  };

  useEffect(() => {
    fetchHealth();
    fetchCacheStats();
    fetchStatusSummary();
    fetchPaymentStats();
  }, []);

  useEffect(() => {
    if (activeTab === 1) {
      fetchAuditLogs();
    }
  }, [activeTab, logFilters]);

  // Handle clear cache
  const handleClearCache = async (cacheType = null) => {
    dispatch(showLoader(true));
    try {
      const result = await dispatch(clearCache({ cacheType }));
      const error = ErrorHandler(result);
      if (error) {
        toast.success('Cache cleared successfully');
        fetchCacheStats();
      }
    } catch (error) {
      toast.error('Failed to clear cache');
    } finally {
      dispatch(showLoader(false));
    }
  };

  // Handle trigger reminders
  const handleTriggerReminders = async () => {
    dispatch(showLoader(true));
    try {
      const result = await dispatch(triggerReminders());
      const error = ErrorHandler(result);
      if (error) {
        toast.success('Reminders triggered successfully');
      }
    } catch (error) {
      toast.error('Failed to trigger reminders');
    } finally {
      dispatch(showLoader(false));
    }
  };

  // Handle run seeds
  const handleRunSeeds = async () => {
    dispatch(showLoader(true));
    try {
      const result = await dispatch(runSeeds({}));
      const error = ErrorHandler(result);
      if (error) {
        toast.success('Seeds completed successfully');
      }
    } catch (error) {
      toast.error('Failed to run seeds');
    } finally {
      dispatch(showLoader(false));
    }
  };

  // Handle run migration
  const handleRunMigration = async () => {
    dispatch(showLoader(true));
    try {
      const result = await dispatch(runMigration({}));
      const error = ErrorHandler(result);
      if (error) {
        toast.success('Migration completed');
      }
    } catch (error) {
      toast.error('Migration failed');
    } finally {
      dispatch(showLoader(false));
    }
  };

  // Format bytes
  const formatBytes = (str) => {
    if (!str) return '0 B';
    return str;
  };

  return (
    <Box>
      {/* Header */}
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Typography variant="h6" fontWeight={600}>
          System Management
        </Typography>
        <Tooltip title="Refresh">
          <IconButton onClick={() => { fetchHealth(); fetchCacheStats(); }}>
            <RefreshIcon />
          </IconButton>
        </Tooltip>
      </Box>

      {/* Tabs */}
      <Tabs value={activeTab} onChange={(e, v) => setActiveTab(v)} sx={{ mb: 3 }}>
        <Tab label="Dashboard" icon={<HealthAndSafetyIcon />} iconPosition="start" />
        <Tab label="Audit Logs" icon={<HistoryIcon />} iconPosition="start" />
        <Tab label="Cache" icon={<CachedIcon />} iconPosition="start" />
        <Tab label="Actions" icon={<PlayArrowIcon />} iconPosition="start" />
      </Tabs>

      {/* Dashboard Tab */}
      {activeTab === 0 && (
        <Grid container spacing={3}>
          {/* Health Card */}
          <Grid item xs={12} md={6}>
            <Card elevation={0} sx={{ border: '1px solid #e0e0e0', height: '100%' }}>
              <CardContent>
                <Box display="flex" alignItems="center" gap={1} mb={2}>
                  <HealthAndSafetyIcon color="success" />
                  <Typography variant="h6">System Health</Typography>
                </Box>
                {health ? (
                  <Box>
                    <Box display="flex" justifyContent="space-between" mb={1}>
                      <Typography color="textSecondary">Status</Typography>
                      <Chip label={health.status} color="success" size="small" />
                    </Box>
                    <Box display="flex" justifyContent="space-between" mb={1}>
                      <Typography color="textSecondary">Database</Typography>
                      <Chip 
                        label={health.database?.status} 
                        color={health.database?.status === 'connected' ? 'success' : 'error'} 
                        size="small" 
                      />
                    </Box>
                    <Box display="flex" justifyContent="space-between" mb={1}>
                      <Typography color="textSecondary">Memory (Heap)</Typography>
                      <Typography>{health.memory?.heapUsed}</Typography>
                    </Box>
                    <Box display="flex" justifyContent="space-between" mb={1}>
                      <Typography color="textSecondary">Uptime</Typography>
                      <Typography>{health.uptime}</Typography>
                    </Box>
                  </Box>
                ) : (
                  <Typography color="textSecondary">Loading...</Typography>
                )}
              </CardContent>
            </Card>
          </Grid>

          {/* Status Summary Card */}
          <Grid item xs={12} md={6}>
            <Card elevation={0} sx={{ border: '1px solid #e0e0e0', height: '100%' }}>
              <CardContent>
                <Box display="flex" alignItems="center" gap={1} mb={2}>
                  <StorageIcon color="primary" />
                  <Typography variant="h6">Member Status Summary</Typography>
                </Box>
                <Grid container spacing={1}>
                  {Object.entries(statusSummary).filter(([k]) => k !== 'total').map(([status, count]) => (
                    <Grid item xs={6} key={status}>
                      <Box display="flex" justifyContent="space-between">
                        <Typography variant="body2" color="textSecondary">{status}</Typography>
                        <Typography variant="body2" fontWeight={500}>{count}</Typography>
                      </Box>
                    </Grid>
                  ))}
                  <Grid item xs={12}>
                    <Box display="flex" justifyContent="space-between" mt={1} pt={1} borderTop="1px solid #e0e0e0">
                      <Typography fontWeight={600}>Total</Typography>
                      <Typography fontWeight={600}>{statusSummary.total || 0}</Typography>
                    </Box>
                  </Grid>
                </Grid>
              </CardContent>
            </Card>
          </Grid>

          {/* Payment Stats Card */}
          <Grid item xs={12}>
            <Card elevation={0} sx={{ border: '1px solid #e0e0e0' }}>
              <CardContent>
                <Typography variant="h6" mb={2}>Payment Statistics</Typography>
                <Grid container spacing={3}>
                  <Grid item xs={6} md={3}>
                    <Typography variant="h4" fontWeight={600} color="primary">
                      {paymentStats.totalTransactions || 0}
                    </Typography>
                    <Typography color="textSecondary">Total Transactions</Typography>
                  </Grid>
                  <Grid item xs={6} md={3}>
                    <Typography variant="h4" fontWeight={600} color="success.main">
                      {paymentStats.successfulTransactions || 0}
                    </Typography>
                    <Typography color="textSecondary">Successful</Typography>
                  </Grid>
                  <Grid item xs={6} md={3}>
                    <Typography variant="h4" fontWeight={600} color="error.main">
                      {paymentStats.failedTransactions || 0}
                    </Typography>
                    <Typography color="textSecondary">Failed</Typography>
                  </Grid>
                  <Grid item xs={6} md={3}>
                    <Typography variant="h4" fontWeight={600} color="success.main">
                      ₹{(paymentStats.totalCollected || 0).toLocaleString()}
                    </Typography>
                    <Typography color="textSecondary">Total Collected</Typography>
                  </Grid>
                </Grid>
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      )}

      {/* Audit Logs Tab */}
      {activeTab === 1 && (
        <Box>
          <Box display="flex" gap={2} mb={2}>
            <FormControl size="small" sx={{ minWidth: 150 }}>
              <InputLabel>Category</InputLabel>
              <Select
                value={logFilters.category}
                label="Category"
                onChange={(e) => setLogFilters({ ...logFilters, category: e.target.value })}
              >
                <MenuItem value="">All</MenuItem>
                <MenuItem value="USER">User</MenuItem>
                <MenuItem value="ADMIN">Admin</MenuItem>
                <MenuItem value="PAYMENT">Payment</MenuItem>
                <MenuItem value="FAMILY">Family</MenuItem>
                <MenuItem value="REFERRAL">Referral</MenuItem>
                <MenuItem value="SYSTEM">System</MenuItem>
              </Select>
            </FormControl>
            <ButtonContained text="Search" onClick={fetchAuditLogs} height="40px" />
          </Box>

          <TableContainer component={Paper} elevation={0} sx={{ border: '1px solid #e0e0e0' }}>
            <Table size="small">
              <TableHead>
                <TableRow sx={{ bgcolor: '#f5f5f5' }}>
                  <TableCell>Timestamp</TableCell>
                  <TableCell>Event</TableCell>
                  <TableCell>Category</TableCell>
                  <TableCell>Description</TableCell>
                  <TableCell>Actor</TableCell>
                  <TableCell>Status</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {auditLogs.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={6} align="center" sx={{ py: 3 }}>
                      <Typography color="textSecondary">No audit logs found</Typography>
                    </TableCell>
                  </TableRow>
                ) : (
                  auditLogs.map((log) => (
                    <TableRow key={log._id} hover>
                      <TableCell>
                        <Typography variant="caption">
                          {new Date(log.createdAt).toLocaleString()}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Chip label={log.eventType} size="small" variant="outlined" />
                      </TableCell>
                      <TableCell>{log.category}</TableCell>
                      <TableCell>{log.description}</TableCell>
                      <TableCell>{log.actorName || log.actorType}</TableCell>
                      <TableCell>
                        <Chip 
                          label={log.status} 
                          size="small" 
                          color={log.status === 'SUCCESS' ? 'success' : 'error'}
                        />
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </Box>
      )}

      {/* Cache Tab */}
      {activeTab === 2 && (
        <Box>
          <Grid container spacing={2}>
            {cacheStats?.stats && Object.entries(cacheStats.stats).map(([name, stats]) => (
              <Grid item xs={12} md={6} lg={3} key={name}>
                <Card elevation={0} sx={{ border: '1px solid #e0e0e0' }}>
                  <CardContent>
                    <Typography variant="subtitle1" fontWeight={600} textTransform="capitalize" mb={2}>
                      {name} Cache
                    </Typography>
                    <Box display="flex" justifyContent="space-between" mb={1}>
                      <Typography color="textSecondary" variant="body2">Keys</Typography>
                      <Typography variant="body2">{stats.keys}</Typography>
                    </Box>
                    <Box display="flex" justifyContent="space-between" mb={1}>
                      <Typography color="textSecondary" variant="body2">Hits</Typography>
                      <Typography variant="body2" color="success.main">{stats.hits}</Typography>
                    </Box>
                    <Box display="flex" justifyContent="space-between" mb={1}>
                      <Typography color="textSecondary" variant="body2">Misses</Typography>
                      <Typography variant="body2" color="error.main">{stats.misses}</Typography>
                    </Box>
                    <ButtonContained 
                      text="Clear" 
                      bgColor="#ff9800" 
                      height="30px"
                      onClick={() => handleClearCache(name)}
                      fullWidth
                    />
                  </CardContent>
                </Card>
              </Grid>
            ))}
          </Grid>
          <Box mt={3}>
            <ButtonContained text="Clear All Caches" bgColor="#d32f2f" onClick={() => handleClearCache()} />
          </Box>
        </Box>
      )}

      {/* Actions Tab */}
      {activeTab === 3 && (
        <Grid container spacing={3}>
          <Grid item xs={12} md={6}>
            <Card elevation={0} sx={{ border: '1px solid #e0e0e0' }}>
              <CardContent>
                <Box display="flex" alignItems="center" gap={1} mb={2}>
                  <NotificationsActiveIcon color="warning" />
                  <Typography variant="h6">Trigger Renewal Reminders</Typography>
                </Box>
                <Typography variant="body2" color="textSecondary" mb={2}>
                  Manually trigger renewal reminder notifications for users with expiring policies.
                </Typography>
                <ButtonContained text="Trigger Reminders" onClick={handleTriggerReminders} />
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} md={6}>
            <Card elevation={0} sx={{ border: '1px solid #e0e0e0' }}>
              <CardContent>
                <Box display="flex" alignItems="center" gap={1} mb={2}>
                  <StorageIcon color="primary" />
                  <Typography variant="h6">Run Database Seeds</Typography>
                </Box>
                <Typography variant="body2" color="textSecondary" mb={2}>
                  Initialize default stages and referral configuration if not already present.
                </Typography>
                <ButtonContained text="Run Seeds" bgColor="#4caf50" onClick={handleRunSeeds} />
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} md={6}>
            <Card elevation={0} sx={{ border: '1px solid #e0e0e0' }}>
              <CardContent>
                <Box display="flex" alignItems="center" gap={1} mb={2}>
                  <HistoryIcon color="secondary" />
                  <Typography variant="h6">Run Data Migration</Typography>
                </Box>
                <Typography variant="body2" color="textSecondary" mb={2}>
                  Migrate existing user data to use new improvement features (stages, family, referrals).
                </Typography>
                <ButtonContained text="Run Migration" bgColor="#9c27b0" onClick={handleRunMigration} />
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      )}
    </Box>
  );
};

export default SystemManagement;
